import { useCallback, useEffect, useRef, useState } from 'react';

import pushModalDataEvent, { ModalDataEvent } from '../util/pushDatalayerEvent';

export const useDelayUnmount = (enabled: boolean, isMounted: boolean, unmountDelay: number) => {
    const timeoutRef = useRef<NodeJS.Timeout | number | undefined>(undefined);
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        if (isMounted && !shouldRender) {
            setShouldRender(true);
        } else if (!isMounted && shouldRender) {
            timeoutRef.current = setTimeout(() => setShouldRender(false), unmountDelay);
        }

        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, [isMounted, shouldRender, setShouldRender, timeoutRef, unmountDelay]);

    if (!enabled) return isMounted;

    return shouldRender;
};

export const useModalRefs = () => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const portalRef = useRef<HTMLDivElement | null>(null);

    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    return { modalRef, contentRef, portalRef, mounted };
};

type ModalTrackingProps = {
    dataTrackName?: string;
    appNamespace?: string;
    title?: string;
    id?: string;
};

export const useModalTracking = ({ dataTrackName, appNamespace, title, id }: ModalTrackingProps) => {
    const [dataEventOpen, setDataEventOpen] = useState<ModalDataEvent>();
    const [dataEventClose, setDataEventClose] = useState<ModalDataEvent>();

    useEffect(() => {
        setDataEventOpen({
            modal_name: title,
            modal_description: dataTrackName ?? id,
            modal_action: 'open',
            app_namespace: appNamespace,
        });

        setDataEventClose({
            modal_name: title,
            modal_description: dataTrackName ?? id,
            modal_action: 'close',
            app_namespace: appNamespace,
        });
    }, [dataTrackName, id, title, appNamespace]);

    const trackOpen = useCallback(() => {
        if (dataEventOpen) pushModalDataEvent(dataEventOpen);
    }, [dataEventOpen]);

    const trackClose = useCallback(() => {
        if (dataEventClose) pushModalDataEvent(dataEventClose);
    }, [dataEventClose]);

    return [trackOpen, trackClose];
};
